import styled from "@emotion/styled";
import { ELEMENT_UL, MARK_BOLD, MARK_ITALIC, MARK_STRIKETHROUGH, MARK_UNDERLINE } from "@udecode/plate";
import { type PlateProps, getNodeTexts, isElement, isMarkActive, useEditorState } from "@udecode/plate-core";
import { useLocalStorage } from "@uidotdev/usehooks";
import produce from "immer";
import { type PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { type BaseEditor, Editor } from "slate";

import { useIsProjectWriter } from "@/auth/permissions";
import { isLockedByOther } from "@/lib/user";
import { useRuleDocument } from "@/pages/documents";
import Switch from "@imminently/decisively-ui/Switch";
import RadioButtons from "@imminently/decisively-ui/RadioButtons";
import type SplitPane from "@imminently/decisively-ui/SplitPane";
import type { ReleaseDocument, RuleDocContentsV3 } from "@packages/commons";
import { useTranslation } from "react-i18next";
import Toolbar from "@imminently/decisively-ui/Toolbar";
import Chip from "@imminently/decisively-ui/Chip";

export enum EditorState {
  source = "source",
  rules = "rules",
  compare = "compare",
}

export type EditorContextType = {
  state: EditorState;
  focused: string;
  isCompare: boolean;
  isSource: boolean;
  isRules: boolean;
  toggleCompare: () => void;
  toggleSource: () => void;
  setEditorState: (state: EditorState) => void;
  paneProps: any; // Replace with the actual type
  editableProps: PlateProps["editableProps"];
  toolbarState: Record<string, EditorToolbarState>;
  setToolbarState: (editor: string, state: EditorToolbarState) => void;
};

export type EditorToolbarState = {
  marks: Record<string, boolean>;
  blocks: Record<string, boolean>;
  currentElement: { type: string; banner?: string; expression?: string } | null;
};

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  padding: 0 1rem;
  gap: 1rem;
  min-height: 3rem;
`;

type SplitProps = React.ComponentProps<typeof SplitPane>;

const getPaneProps = (state: EditorState): any => {
  switch (state) {
    case EditorState.source:
      return {
        sizes: [100, 0],
        minSize: [100, 0],
        maxSize: [100, 0],
        gutterSize: 0,
        gutterStyle: () => ({ display: "none" }),
      };
    case EditorState.rules:
      return {
        sizes: [0, 100],
        minSize: [0, 100],
        maxSize: [0, 100],
        gutterSize: 0,
        gutterStyle: () => ({ display: "none" }),
      };
    case EditorState.compare:
      return {
        sizes: [50, 50],
        minSize: [300, 300],
        // gutterSize: 8,
        gutterStyle: () => ({ display: "block", width: "8px" }),
      };
  }
};

export const EditorContext = createContext<EditorContextType | undefined>(undefined);

type DocumentSettings = {
  editorState: EditorState | null;
};

type StoredSettings = Record<string, DocumentSettings>;

const defaultSettings = (): DocumentSettings => ({
  editorState: null,
});

const DOCUMENT_SETTINGS_KEY = "immi.settings.documents";

// load user persist settings for the document stored in localStorage
const usePersistedSettings = (id: string) => {
  // using new useLocalStorage hook, as the react-use one is being weird with TS
  // our custom one isnt exporting correctly in the package
  const [settings, setSettings] = useLocalStorage<StoredSettings>(DOCUMENT_SETTINGS_KEY, {});
  // get doc settings
  const docSettings = settings[id] ?? defaultSettings();

  // editor state set function
  const storeEditorState = (state: EditorState) => {
    const newState = produce(settings, (draft) => {
      if (!draft[id]) {
        draft[id] = defaultSettings();
      }
      draft[id].editorState = state;
    });
    setSettings(newState);
  };

  // don't export full setSettings, as we want to control the shape of the settings object
  return { settings: docSettings, storeEditorState };
};

const getText = (node) =>
  Array.from(getNodeTexts(node))
    .map(([n]) => n.text)
    .filter((t) => t.trim().length > 0)
    .join(" ");

const isNotEmpty = (data: any[]) => {
  return data && data.length > 0 && data.some((n) => n && getText(n).length > 0);
};

export const EditorProvider = ({ document, children }: { document: ReleaseDocument } & PropsWithChildren) => {
  const isProjectWriter = useIsProjectWriter();
  const { settings, storeEditorState } = usePersistedSettings(document.reference);
  const { document: ruleDocument } = useRuleDocument();
  const { source, rules } = ruleDocument.contents as RuleDocContentsV3;

  // we want to keep track of this vs the stored separately, as we want this to re-calculate on render
  const [editorState, setState] = useState<EditorState>(() => {
    const { editorState: storedState } = settings;
    const hasSource = isNotEmpty(source);
    const hasRules = isNotEmpty(rules);
    // const hasBoth = hasSource && hasRules;
    // const bothEmpty = !hasSource && !hasRules;
    // return storedState ?? (hasBoth || bothEmpty ? EditorState.compare : hasSource ? EditorState.source : EditorState.rules);
    // UPDATE reverted to only auto detect rules only, otherwise always show both
    return storedState ?? (hasRules && !hasSource ? EditorState.rules : EditorState.compare);
  });

  const setEditorState = (state: EditorState) => {
    storeEditorState(state);
    setState(state);
  };

  const [focusedEditor, setFocusedEditor] = useState<EditorState>(() =>
    editorState === EditorState.compare ? EditorState.source : editorState,
  );
  const [toolbarState, setToolbarStateState] = useState<Record<string, EditorToolbarState>>({});

  const setToolbarState = (editor: string, state: EditorToolbarState) => {
    setToolbarStateState((prev) => {
      const current = prev[editor];
      if (
        current &&
        current.marks[MARK_BOLD] === state.marks[MARK_BOLD] &&
        current.marks[MARK_ITALIC] === state.marks[MARK_ITALIC] &&
        current.marks[MARK_UNDERLINE] === state.marks[MARK_UNDERLINE] &&
        current.marks[MARK_STRIKETHROUGH] === state.marks[MARK_STRIKETHROUGH] &&
        current.blocks[ELEMENT_UL] === state.blocks[ELEMENT_UL] &&
        current.currentElement?.type === state.currentElement?.type &&
        current.currentElement?.banner === state.currentElement?.banner &&
        current.currentElement?.expression === state.currentElement?.expression
      ) {
        return prev;
      }

      return { ...prev, [editor]: state };
    });
  };

  const paneProps = getPaneProps(editorState);

  const editableProps: PlateProps["editableProps"] = {
    autoFocus: editorState !== EditorState.compare,
    readOnly:
      !isProjectWriter || isLockedByOther(document.locked),
    onFocus: (e) => {
      const id = e.target.getAttribute("data-slate-editor-id");
      if (id) {
        setFocusedEditor(id as EditorState);
      }
    },
  };

  const focused = editorState === EditorState.compare ? focusedEditor : editorState;
  const isCompare = editorState === EditorState.compare;
  const isSource = editorState === EditorState.source;
  const isRules = editorState === EditorState.rules;

  const toggleCompare = () => {
    setEditorState(isCompare ? EditorState.source : EditorState.compare);
  };

  const toggleSource = () => {
    setEditorState(isSource ? EditorState.rules : EditorState.source);
  };

  return (
    <EditorContext.Provider
      value={{
        state: editorState,
        focused,
        isCompare,
        isSource,
        isRules,
        toggleCompare,
        toggleSource,
        setEditorState,
        paneProps,
        editableProps,
        toolbarState,
        setToolbarState,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export const useMultiEditor = () => {
  const context = useContext(EditorContext);
  if (context === undefined) {
    throw new Error("useMultiEditor must be used within a EditorProvider");
  }
  return context;
};

export const useOptionalMultiEditor = () => useContext(EditorContext);

export const ToolbarStateBridge = ({ editor: editorId }: { editor: EditorState }) => {
  const editor = useEditorState();
  const context = useOptionalMultiEditor();

  useEffect(() => {
    if (!context) {
      return;
    }

    const currentElementEntry = editor.selection
      ? Editor.above(editor as BaseEditor, {
          at: editor.selection,
          match: (node) => isElement(node),
        })
      : undefined;
    const currentElement = currentElementEntry?.[0] as { type?: string; banner?: string; expression?: string } | undefined;

    context.setToolbarState(editorId, {
      marks: getToolbarMarks(editor),
      blocks: getToolbarBlocks(editor),
      currentElement: currentElement?.type
        ? { type: currentElement.type, banner: currentElement.banner, expression: currentElement.expression }
        : null,
    });
  });

  return null;
};

export const getToolbarMarks = (editor: Parameters<typeof isMarkActive>[0]) => ({
  [MARK_BOLD]: isMarkActive(editor, MARK_BOLD),
  [MARK_ITALIC]: isMarkActive(editor, MARK_ITALIC),
  [MARK_UNDERLINE]: isMarkActive(editor, MARK_UNDERLINE),
  [MARK_STRIKETHROUGH]: isMarkActive(editor, MARK_STRIKETHROUGH),
});

export const getToolbarBlocks = (editor: Parameters<typeof isMarkActive>[0]) => ({
  [ELEMENT_UL]: Boolean(
    editor.selection &&
      Editor.above(editor as BaseEditor, {
        at: editor.selection,
        match: (node) => (node as { type?: unknown }).type === ELEMENT_UL,
      }),
  ),
});

export type EditorControlsProps = {
  editor: EditorState;
};

export const EditorControls = ({ editor }: EditorControlsProps) => {
  const { state, setEditorState } = useMultiEditor();
  const { t } = useTranslation();
  const hideCompare = state === EditorState.compare && editor === EditorState.source;

  const setTabHandler = (value: string) => {
    setEditorState(value as EditorState);
  };

  const tabs = [
    { label: t("metadata.source"), value: EditorState.source },
    { label: t("build.rules"), value: EditorState.rules },
  ];

  return (
    <ControlBar>
      {state !== EditorState.compare ? (
        <RadioButtons
          name="editor_view"
          value={state}
          onValueChange={setTabHandler}
          options={tabs}
        />
      ) : (
        <Chip variant="secondary" size="sm" className="capitalize">
          {/* find label in tabs */}
          {tabs.find((tab) => tab.value === editor)?.label ?? editor}
        </Chip>
      )}
      <Toolbar.FillSpace />
      {!hideCompare && (
        <div className="flex items-center gap-2">
          <label htmlFor="compare-toggle" className="cursor-pointer text-sm">
            {t("documents.side_by_side")}
          </label>
          <Switch
            id="compare-toggle"
            value={state === EditorState.compare}
            onValueChange={(checked) => setEditorState(checked ? EditorState.compare : EditorState.source)}
          />
        </div>
      )}
    </ControlBar>
  );
};
